import * as THREE from 'three';
import { SceneModule, SceneContext, EnvironmentState } from '../types';
import { CameraView } from '../../engine/cameraRig';
import { WeatherType } from '../../app/store';
import { StylizedWater } from '../shared/water';
import { disposeHierarchy } from '../../engine/disposal';
import { t } from '../../ui/i18n';
import { store } from '../../app/store';

export class ErhaiScene implements SceneModule {
  public id: string = 'erhai';
  public nameKey: string = 'scene_erhai';

  public root: THREE.Group = new THREE.Group();
  private lakeWater!: StylizedWater;
  private reflectionPoolWater!: StylizedWater;
  private threePagodasGroup!: THREE.Group;
  private drivableBoat!: THREE.Group;
  private gullsGroup!: THREE.Group;
  private lanternBoats: THREE.Group[] = [];

  // Drivable boat state
  private boatPos: THREE.Vector3 = new THREE.Vector3(0, 0.35, 12);
  private boatAngle: number = 0;
  private boatSpeed: number = 0;
  private isDrivingView: boolean = false;
  private showToastFn?: (msg: string) => void;
  private sceneContext?: SceneContext;

  public cameras: CameraView[] = [
    {
      id: 1,
      nameKey: 'view_erhai_1',
      position: new THREE.Vector3(0, 32, 55),
      target: new THREE.Vector3(0, 4, 0),
      mode: 'orbit',
      minDistance: 12,
      maxDistance: 110,
    },
    {
      id: 2,
      nameKey: 'view_erhai_2',
      position: new THREE.Vector3(-22, 6, -6),
      target: new THREE.Vector3(-22, 10, -22),
      mode: 'orbit',
      minDistance: 6,
      maxDistance: 60,
    },
    {
      id: 3,
      nameKey: 'view_erhai_3',
      position: new THREE.Vector3(22, 3.5, -4),
      target: new THREE.Vector3(18, 2.5, -12),
      mode: 'orbit',
      minDistance: 3,
      maxDistance: 45,
    },
    {
      id: 4,
      nameKey: 'view_erhai_4',
      position: new THREE.Vector3(4, 3.8, 16),
      target: new THREE.Vector3(0, 1.5, 0),
      mode: 'orbit',
      minDistance: 4,
      maxDistance: 50,
    },
    {
      id: 5,
      nameKey: 'view_erhai_5',
      position: new THREE.Vector3(0, 18, 10),
      target: new THREE.Vector3(0, 2, -15),
      mode: 'fly',
    },
    {
      id: 6,
      nameKey: 'view_erhai_6',
      position: new THREE.Vector3(0, 4.5, 20),
      target: new THREE.Vector3(0, 1.0, 12),
      mode: 'boat',
    },
  ];

  public async load(ctx: SceneContext, onProgress: (p: number) => void): Promise<void> {
    this.sceneContext = ctx;
    this.showToastFn = ctx.showToast;
    this.root.name = 'ErhaiLakeScene';

    onProgress(0.1);
    this.createCangshanMountains();

    onProgress(0.3);
    // Vast Erhai lake water
    this.lakeWater = new StylizedWater({
      width: 110,
      height: 80,
      deepColor: 0x0284c7, // serene sapphire cyan
      shallowColor: 0x7dd3fc, // pale sky reflection
    });
    this.lakeWater.mesh.position.set(0, 0.05, 10);
    this.root.add(this.lakeWater.mesh);

    onProgress(0.5);
    // Three Pagodas of Chongsheng Temple & Mirror Reflecting Pool
    this.createThreePagodas();

    onProgress(0.65);
    // Bai ethnic village & flower fields (rapeseed yellow & cosmos pink)
    this.createBaiVillageAndFields();

    onProgress(0.8);
    // Drivable Boat in View 6
    this.createDrivableBoat();

    // Flying seagulls
    this.createSeagulls();

    // Raycast interaction
    this.setupInteractivity(ctx);

    ctx.sceneRoot.add(this.root);
    onProgress(1.0);
  }

  private createCangshanMountains(): void {
    // 19 Peaks of Cangshan on western horizon
    const mtnGroup = new THREE.Group();
    mtnGroup.position.set(0, 0, -38);

    const rockMat = new THREE.MeshStandardMaterial({ color: 0x334155, flatShading: true });
    const snowMat = new THREE.MeshStandardMaterial({ color: 0xe2e8f0, flatShading: true });

    for (let i = -7; i <= 7; i++) {
      const h = 24 - Math.abs(i) * 1.2 + Math.sin(i * 1.5) * 3;
      const r = 8;
      const peak = new THREE.Mesh(new THREE.ConeGeometry(r, h, 6), rockMat);
      peak.position.set(i * 7.5, h / 2, 0);
      mtnGroup.add(peak);

      // Light snow dusting on peaks
      const cap = new THREE.Mesh(new THREE.ConeGeometry(r * 0.45, h * 0.35, 6), snowMat);
      cap.position.set(i * 7.5, h * 0.85, 0);
      mtnGroup.add(cap);
    }

    // "Jade Belt" (Ngọc Đới Vân) horizontal cloud ribbon across Cangshan
    const beltMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.8,
      roughness: 0.9,
    });
    const beltGeo = new THREE.CylinderGeometry(2.0, 2.0, 110, 8);
    beltGeo.rotateZ(Math.PI / 2);
    const belt = new THREE.Mesh(beltGeo, beltMat);
    belt.position.set(0, 14, 2);
    mtnGroup.add(belt);

    this.root.add(mtnGroup);
  }

  private createThreePagodas(): void {
    this.threePagodasGroup = new THREE.Group();
    this.threePagodasGroup.position.set(-22, 0, -22);

    // Stone base plaza
    const plazaGeo = new THREE.BoxGeometry(22, 1, 20);
    const plazaMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, roughness: 0.8 });
    const plaza = new THREE.Mesh(plazaGeo, plazaMat);
    plaza.position.y = 0.5;
    this.threePagodasGroup.add(plaza);

    // Reflecting mirror pool
    this.reflectionPoolWater = new StylizedWater({
      width: 14,
      height: 8,
      deepColor: 0x075985,
      shallowColor: 0x38bdf8,
    });
    this.reflectionPoolWater.mesh.position.set(0, 1.05, 4);
    this.threePagodasGroup.add(this.reflectionPoolWater.mesh);

    // Qianxun Main Pagoda (tall, square, 16 tiers)
    const pagodaMat = new THREE.MeshStandardMaterial({ color: 0xd6d3d1, roughness: 0.7 });
    const mainTower = this.createSinglePagoda(16, 18, 3.6, pagodaMat);
    mainTower.position.set(0, 1.0, -4);
    this.threePagodasGroup.add(mainTower);

    // Two flanking smaller pagodas (octagonal, 10 tiers)
    const leftTower = this.createSinglePagoda(10, 11, 2.2, pagodaMat);
    leftTower.position.set(-6.5, 1.0, -1);
    this.threePagodasGroup.add(leftTower);

    const rightTower = this.createSinglePagoda(10, 11, 2.2, pagodaMat);
    rightTower.position.set(6.5, 1.0, -1);
    this.threePagodasGroup.add(rightTower);

    this.root.add(this.threePagodasGroup);
  }

  private createSinglePagoda(tiers: number, totalHeight: number, baseWidth: number, mat: THREE.Material): THREE.Group {
    const group = new THREE.Group();
    const tierHeight = totalHeight / tiers;

    for (let t = 0; t < tiers; t++) {
      const alpha = t / tiers;
      const width = baseWidth * (1.0 - alpha * 0.45);
      const y = t * tierHeight;

      const bodyGeo = new THREE.BoxGeometry(width, tierHeight * 0.75, width);
      const body = new THREE.Mesh(bodyGeo, mat);
      body.position.y = y + tierHeight * 0.38;
      group.add(body);

      // Overhanging tier cornice
      const corniceGeo = new THREE.BoxGeometry(width * 1.25, tierHeight * 0.25, width * 1.25);
      const cornice = new THREE.Mesh(corniceGeo, mat);
      cornice.position.y = y + tierHeight * 0.88;
      group.add(cornice);
    }

    // Top finial
    const finialGeo = new THREE.ConeGeometry(baseWidth * 0.25, 2.0, 6);
    const finialMat = new THREE.MeshStandardMaterial({ color: 0xf59e0b, metalness: 0.5 });
    const finial = new THREE.Mesh(finialGeo, finialMat);
    finial.position.y = totalHeight + 1.0;
    group.add(finial);

    return group;
  }

  private createBaiVillageAndFields(): void {
    // Village on Eastern shore
    const villageGroup = new THREE.Group();
    villageGroup.position.set(22, 0, -14);

    const whiteWallMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.7 });
    const blackEavesMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.5 });

    // Bai style courtyard houses (Sanfang Yizhaobi)
    for (let i = 0; i < 9; i++) {
      const hx = (i % 3) * 7;
      const hz = Math.floor(i / 3) * 7;

      const house = new THREE.Group();
      house.position.set(hx, 0, hz);

      // White walls
      const wall = new THREE.Mesh(new THREE.BoxGeometry(5.0, 3.2, 4.0), whiteWallMat);
      wall.position.y = 1.6;
      house.add(wall);

      // Upturned flying black eaves
      const roof = new THREE.Mesh(new THREE.ConeGeometry(4.2, 1.4, 4), blackEavesMat);
      roof.rotateY(Math.PI / 4);
      roof.position.y = 3.9;
      house.add(roof);

      villageGroup.add(house);
    }

    // Flower fields: Rapeseed yellow & Cosmos pink
    const yellowFieldMat = new THREE.MeshStandardMaterial({ color: 0xfacc15, roughness: 0.9 });
    const pinkFieldMat = new THREE.MeshStandardMaterial({ color: 0xf472b6, roughness: 0.9 });

    const yellowField = new THREE.Mesh(new THREE.BoxGeometry(16, 0.4, 12), yellowFieldMat);
    yellowField.position.set(6, 0.2, 10);
    villageGroup.add(yellowField);

    const pinkField = new THREE.Mesh(new THREE.BoxGeometry(14, 0.4, 10), pinkFieldMat);
    pinkField.position.set(6, 0.2, 22);
    villageGroup.add(pinkField);

    // Wooden boat jetty extending into the lake
    const jettyGeo = new THREE.BoxGeometry(3.0, 0.4, 14.0);
    const jettyMat = new THREE.MeshStandardMaterial({ color: 0x78350f, roughness: 0.85 });
    const jetty = new THREE.Mesh(jettyGeo, jettyMat);
    jetty.position.set(-8, 0.35, 12);
    villageGroup.add(jetty);

    this.root.add(villageGroup);
  }

  private createDrivableBoat(): void {
    this.drivableBoat = new THREE.Group();
    this.drivableBoat.position.copy(this.boatPos);

    const boatMat = new THREE.MeshStandardMaterial({ color: 0x92400e, roughness: 0.8 });
    const canopyMat = new THREE.MeshStandardMaterial({ color: 0x1e3a8a, roughness: 0.5 }); // blue canopy

    // Hull
    const hullGeo = new THREE.BoxGeometry(1.6, 0.6, 4.2);
    const hull = new THREE.Mesh(hullGeo, boatMat);
    hull.position.y = 0.3;
    this.drivableBoat.add(hull);

    // Tapered bow
    const bowGeo = new THREE.ConeGeometry(0.8, 1.2, 3);
    bowGeo.rotateX(Math.PI / 2);
    const bow = new THREE.Mesh(bowGeo, boatMat);
    bow.position.set(0, 0.3, -2.6);
    this.drivableBoat.add(bow);

    // Canopy shelter
    const canopyGeo = new THREE.BoxGeometry(1.4, 0.8, 2.0);
    const canopy = new THREE.Mesh(canopyGeo, canopyMat);
    canopy.position.set(0, 1.0, 0.2);
    this.drivableBoat.add(canopy);

    // Lantern on bow
    const bowLantern = new THREE.Mesh(
      new THREE.SphereGeometry(0.2, 6, 6),
      new THREE.MeshStandardMaterial({ color: 0xffedd5, emissive: 0xf59e0b, emissiveIntensity: 1.5 })
    );
    bowLantern.position.set(0, 0.7, -2.2);
    this.drivableBoat.add(bowLantern);

    this.root.add(this.drivableBoat);
  }

  private createSeagulls(): void {
    this.gullsGroup = new THREE.Group();
    const gullMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.5 });

    for (let i = 0; i < 9; i++) {
      const gull = new THREE.Group();
      const body = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.8, 4), gullMat);
      body.rotateX(Math.PI / 2);
      gull.add(body);

      // Wings
      const wingL = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.04, 0.25), gullMat);
      wingL.position.x = -0.45;
      gull.add(wingL);

      const wingR = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.04, 0.25), gullMat);
      wingR.position.x = 0.45;
      gull.add(wingR);

      gull.position.set(
        (Math.random() - 0.5) * 35,
        4.0 + Math.random() * 6.0,
        (Math.random() - 0.5) * 35
      );

      this.gullsGroup.add(gull);
    }

    this.root.add(this.gullsGroup);
  }

  private setupInteractivity(ctx: SceneContext): void {
    const canvas = ctx.renderer.domElement;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const onPointerDown = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, ctx.camera);

      // Feeding seagulls (Toy: Click on lake or sky triggers gulls dive)
      const waterHits = raycaster.intersectObject(this.lakeWater.mesh);
      if (waterHits.length > 0) {
        this.feedSeagulls(waterHits[0].point);
        this.showToastFn?.(t('toy_gulls_fed', store.getState().language));
      }
    };

    if (canvas && typeof canvas.addEventListener === 'function') {
      canvas.addEventListener('pointerdown', onPointerDown);
    }
  }

  public feedSeagulls(targetPos: THREE.Vector3): void {
    // Make seagulls circle and dive toward target
    for (const gull of this.gullsGroup.children) {
      gull.position.lerp(new THREE.Vector3(targetPos.x + (Math.random() - 0.5) * 4, 1.2, targetPos.z + (Math.random() - 0.5) * 4), 0.4);
    }
  }

  public update(dt: number, env: EnvironmentState): void {
    const time = performance.now() / 1000;

    // Emissive lantern boats glow stronger at dusk and night
    for (const lb of this.lanternBoats) {
      lb.position.z += dt * 0.5;
      const mesh = lb.children[0] as THREE.Mesh;
      if (mesh && mesh.material instanceof THREE.MeshStandardMaterial) {
        mesh.material.emissiveIntensity = 0.5 + env.emissiveFactor * 1.5;
      }
    }

    // Check if user is in View 6 (Drivable Boat view)
    const isView6 = store.getState().cameraView === 6;
    this.isDrivingView = isView6;

    // Boat driving logic (Arrow keys / Joystick)
    if (this.isDrivingView && this.sceneContext) {
      const move = this.sceneContext.getBoatMoveVector?.() || new THREE.Vector2(0, 0);

      // Turn
      this.boatAngle -= move.x * 2.2 * dt;

      // Forward/Back
      const targetSpeed = move.y * 10.0;
      this.boatSpeed = THREE.MathUtils.lerp(this.boatSpeed, targetSpeed, dt * 3.0);

      const fwd = new THREE.Vector3(-Math.sin(this.boatAngle), 0, -Math.cos(this.boatAngle));
      this.boatPos.addScaledVector(fwd, this.boatSpeed * dt);

      // Keep boat within lake boundaries
      this.boatPos.x = THREE.MathUtils.clamp(this.boatPos.x, -45, 45);
      this.boatPos.z = THREE.MathUtils.clamp(this.boatPos.z, -25, 40);

      this.drivableBoat.position.copy(this.boatPos);
      this.drivableBoat.rotation.y = this.boatAngle;

      // Camera follows boat in view 6
      const camTarget = this.boatPos.clone().add(new THREE.Vector3(0, 1.2, 0));
      const camOffset = new THREE.Vector3(
        Math.sin(this.boatAngle) * 8.0,
        3.5,
        Math.cos(this.boatAngle) * 8.0
      );
      this.cameras[5].position.copy(this.boatPos).add(camOffset);
      this.cameras[5].target.copy(camTarget);
    }

    // Natural bobbing of boat
    this.drivableBoat.position.y = 0.35 + Math.sin(time * 2.5) * 0.04;

    // Seagulls circling and flapping wings
    for (let i = 0; i < this.gullsGroup.children.length; i++) {
      const gull = this.gullsGroup.children[i];
      const angle = time * 0.6 + i * (Math.PI / 4);
      gull.position.x += Math.cos(angle) * dt * 3.0;
      gull.position.z += Math.sin(angle) * dt * 3.0;
      gull.position.y = 4.0 + Math.sin(time * 2.0 + i) * 1.5;
      gull.rotation.y = -angle;

      // Wing flap
      const wingL = gull.children[1];
      const wingR = gull.children[2];
      if (wingL && wingR) {
        wingL.rotation.z = Math.sin(time * 8.0 + i) * 0.35;
        wingR.rotation.z = -Math.sin(time * 8.0 + i) * 0.35;
      }
    }

    this.lakeWater.update(dt);
    this.reflectionPoolWater.update(dt);
  }

  public setTimeOfDay(_t: number): void {}
  public setWeather(_w: WeatherType): void {}

  public getSuggestedTracks(): string[] {
    return ['erhai-morning-sun', 'dali-breeze-flute'];
  }

  public onToyAction(toyId: string): void {
    if (toyId === 'gulls') {
      this.feedSeagulls(new THREE.Vector3(0, 0, 10));
      this.showToastFn?.(t('toy_gulls_fed', store.getState().language));
    }
  }

  public dispose(): void {
    disposeHierarchy(this.root);
    this.lakeWater.dispose();
    this.reflectionPoolWater.dispose();
    this.lanternBoats = [];
  }
}
